import {kmsQueries} from '@domain/kiln-monitoring'
import {appUrl} from '@hconnect/common/hproduce/config'
import {SETTINGS_HOST} from '@hconnect/common/hproduce/config/settings'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {Box, Button, Portal} from '@mui/material'
import {useQuery} from '@tanstack/react-query'
import {useKiln, KilnSchematicPanel} from '@widgets/kms'
import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {usePlantPermission} from '../../../shared/hooks/useMaintainPermission'
import {useUrlParam} from '../../../shared/hooks/useUrlParam'
import {SignalTreeContainer} from '../../general/components/SignalTree/SignalTree'
import {NodeSignalData} from '../../general/components/SignalTree/SignalTreeNode'
import {usePlantMonitoring} from '../../general/context/PlantMonitoringContext'
import {CardWrapper} from '../../shared/components/styled'
import {UserSignalGroupsConfig} from '../../shared/types/sensor.types'

export const ModelWrapper = () => {
  const [hoveredSignal, setHoveredSignal] = useState<string | null>(null)
  const {kilnId, plantUpmId} = useKiln()
  const {t} = useTranslation()
  const navigate = useNavigate()
  const plantId = useUrlParam('plantId')
  const {actions, state} = usePlantMonitoring()
  const canConfigureKiln = usePlantPermission('CREATE_MASTER_DATA')

  const handleSvgClickCb = useCallback(
    (localNames: string[]) => actions.selectSignals(localNames),
    [actions]
  )

  const handleKilnMetricClick = useCallback(
    (localNames: string[]) => actions.selectSignals(localNames),
    [actions]
  )

  const handleSignalHover = useCallback(
    (data: NodeSignalData | null) => setHoveredSignal(data?.localName ?? null),
    []
  )

  const handleAreaClick = useCallback(
    (kilnId: string, area: string) => navigate('kms/' + kilnId + '?area=' + area),
    [navigate]
  )

  const handleCrankClick = useCallback(() => navigate('crank'), [navigate])

  const handleKilnSwitch = useCallback((kilnId: string) => navigate('../' + kilnId), [navigate])

  const headerActions = useMemo(
    () =>
      canConfigureKiln ? (
        <Box display={{xs: 'none', sm: 'flex'}} alignItems="flex-start" px={2} py={1}>
          <Button
            variant="text"
            color="primary"
            startIcon={<EditOutlinedIcon />}
            sx={{
              minWidth: 'fit-content',
              maxHeight: 48,
              whiteSpace: 'nowrap'
            }}
            href={`${appUrl(SETTINGS_HOST)}/plants/${plantId}/kilnConfig/${kilnId}/config`}
          >
            {t('kilnConfig.configLink')}
          </Button>
        </Box>
      ) : undefined,
    [canConfigureKiln, plantId, kilnId, t]
  )

  const {data: kmsSignals} = useQuery(kmsQueries.signals(kilnId, plantUpmId))

  const signalGroups =
    kmsSignals?.reduce(
      (acc, signal) => {
        if (signal.group) {
          if (!acc[signal.group]) {
            acc[signal.group] = [signal.localName]
          } else {
            acc[signal.group].push(signal.localName)
          }
        }
        return acc
      },
      {} as Record<string, string[]>
    ) || {}

  const groupsConfig: UserSignalGroupsConfig = {
    disableEdit: true,
    masterUpmId: kilnId,
    predefinedGroups: Object.entries(signalGroups).map(([name, signals]) => ({
      name,
      signals,
      combined: [],
      plantId,
      columns: 2,
      chartsOrder: signals,
      masterUpmId: kilnId,
      quickSelection: null,
      type: 'predefined'
    }))
  }

  return (
    <React.Fragment>
      <Portal container={document.getElementById('signal-tree')}>
        <SignalTreeContainer
          forceEquipmentOnly
          onSignalHover={handleSignalHover}
          groupsConfig={groupsConfig}
        />
      </Portal>
      <CardWrapper
        display="flex"
        pl={3}
        pb={1}
        flexDirection="column"
        gap={2}
        data-test-id="kiln-model-card"
        style={
          state.signalCharts.length ? {borderBottomLeftRadius: 0, borderBottomRightRadius: 0} : {}
        }
      >
        <KilnSchematicPanel
          hoveredSignal={hoveredSignal}
          svgClickCb={handleSvgClickCb}
          onKilnMetricClick={handleKilnMetricClick}
          headerActions={headerActions}
          onAreaClick={handleAreaClick}
          onCrankClick={handleCrankClick}
          onKilnSwitch={handleKilnSwitch}
        />
      </CardWrapper>
    </React.Fragment>
  )
}
